import { useEffect } from 'react';
import {
  setJWT,
  setActiveProfileId,
  clearAuthState,
  isCookieMode,
  post as apiPost,
  put as apiPut,
  get as apiGet,
  setTokenRefreshHandler,
  setLogoutInProgress,
} from '../lib/api/client';
import { isApiError } from '../lib/api/errors';
import { CSRF_COOKIE_NAME, isAuthEnabled } from './sessionCookieNames';
import { setActiveProfile, getById as getProfileById, patch as patchProfile } from './profileService';
import { setLogLevels } from './loggerService';
import type { LoginResponse } from '../types/loginResponse';
import type { User } from '../types/user';
import type { Profile } from '../types/profile';
import { useAuthStore } from '../store/useAuthStore';
import { getDevice, setDeviceUserId, putDeviceNow, logoutDeviceOnBackend } from './deviceService';
import { identifyPosthogProfile, resetPosthog } from './posthogService';
import { disconnectAll as disconnectWs } from './wsService';
import { SUPPORTED_PERSONAS, personaQueryParam } from '../utils/personas';
import { JWT_STORAGE_KEY, EMAIL_STORAGE_KEY, SECRET_STORAGE_KEY } from './authStorageKeys';

/**
 * Admin sign-in and session lifecycle.
 *
 * Differences from hp-app-core-react's authService, all deliberate:
 *
 *   - No `/exists` and no `/register`. `/exists` creates a pending CONSUMER
 *     profile for an unknown email; the admin app must never create accounts.
 *     Admin users are onboarded operationally with a company-persona profile.
 *   - Sign-in is `/login?persona=company` (password) or
 *     `PUT /access-code/email` + `/login-code` (one-time code). The access-code
 *     endpoint 404s an unknown email rather than creating a user.
 *   - `whoAmI` selects the company profile and sets `sessionStatus`, so an
 *     account without one gets a clear refusal (see useAuthStore).
 *   - No location, UTM or onboarding plumbing.
 */

const LOGIN_PERSONA = SUPPORTED_PERSONAS[0] ?? 'company';

/** Thrown by whoAmI when the account has no profile with an admin persona. */
export class NoAdminProfileError extends Error {
  constructor() {
    super('No company profile on this account');
    this.name = 'NoAdminProfileError';
  }
}

async function refreshSession(): Promise<boolean> {
  // Cookie mode: the API client refreshes via /auth/refresh on its own.
  if (isCookieMode) return true;

  const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
  const storedSecret = localStorage.getItem(SECRET_STORAGE_KEY);
  if (!storedEmail || !storedSecret) {
    await logout();
    return false;
  }
  try {
    await loginWithSecret(storedEmail, storedSecret);
    return true;
  } catch {
    await logout();
    return false;
  }
}

/**
 * Restores the session in the background at boot. `isAuthenticated` was
 * already hydrated from the CSRF-cookie sentinel, so the shell paints
 * optimistically; this confirms it and loads the company profile.
 */
export async function restoreSession(): Promise<void> {
  const store = useAuthStore.getState();
  if (!isAuthEnabled || !store.isAuthenticated) {
    store.setSessionStatus('idle');
    await putDeviceNow();
    return;
  }
  if (!isCookieMode) {
    const storedJwt = localStorage.getItem(JWT_STORAGE_KEY);
    if (storedJwt) setJWT(storedJwt);
  }
  store.setSessionStatus('restoring');
  try {
    await whoAmI();
    store.setAuthenticated(true);
  } catch (err) {
    // A 401 that survived the client's refresh means there is no usable
    // session behind the sentinel cookie (expired, another cookie family, a
    // stale cookie). That is "signed out", not a server problem: clear it
    // and let the shell send the user to sign-in.
    if (isApiError(err) && err.status === 401) {
      await logoutSoft();
      return;
    }
    // Other auth failures were handled by the client's refresh → logout
    // path. Anything else is transient: keep the cached state and show the
    // retryable error screen rather than logging out.
    if (!useAuthStore.getState().isAuthenticated) return;
    if (!(err instanceof NoAdminProfileError)) store.setSessionStatus('error');
    await putDeviceNow();
  }
}

export function InitAuth() {
  useEffect(() => {
    setTokenRefreshHandler(refreshSession);
    void restoreSession();
    return () => setTokenRefreshHandler(null);
  }, []);
  return null;
}

function persistLocalCredentials(response: LoginResponse, email?: string) {
  if (!isCookieMode) {
    if (response.jwt) {
      setJWT(response.jwt);
      localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
    }
    if (response.secret) localStorage.setItem(SECRET_STORAGE_KEY, response.secret);
  }
  if (email) localStorage.setItem(EMAIL_STORAGE_KEY, email);
}

async function completeLogin(response: LoginResponse): Promise<void> {
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setSessionStatus('restoring');
  await whoAmI();
  await setDeviceUserId(response.userId);
}

export async function loginWithEmailAndPassword(email: string, password: string, stayLoggedIn = false): Promise<void> {
  const response = await apiPost<LoginResponse>(
    '/login',
    {
      email,
      password,
      includeToken: false,
      includeSecret: true,
      stayLoggedIn,
      identifier: getDevice()?.identifier,
      persona: LOGIN_PERSONA,
    },
    { persona: LOGIN_PERSONA },
  );
  persistLocalCredentials(response, email);
  await completeLogin(response);
}

/** Emails a one-time sign-in code. 404s (no account) rather than creating one. */
export async function sendAccessCode(email: string): Promise<void> {
  await apiPut<void>('/access-code/email', {}, { email, persona: LOGIN_PERSONA });
}

export async function loginWithCode(email: string, code: string, stayLoggedIn = false): Promise<void> {
  const response = await apiPost<LoginResponse>('/login-code', {
    email,
    password: code,
    includeToken: false,
    includeSecret: true,
    stayLoggedIn,
    identifier: getDevice()?.identifier,
    persona: LOGIN_PERSONA,
  });
  persistLocalCredentials(response, email);
  await completeLogin(response);
}

export async function loginWithSecret(email: string, secret: string): Promise<void> {
  const response = await apiPost<LoginResponse>('/login-secret', {
    email,
    secret,
    includeToken: false,
    includeSecret: true,
    persona: LOGIN_PERSONA,
  });
  persistLocalCredentials(response);
  await completeLogin(response);
}

/**
 * Exchanges a `?s=<secret>` link parameter for a session (links in alert
 * emails / SMS). Throws on failure; a 401 means the secret expired and the
 * caller offers a resend.
 */
export async function loginFromUrlSecret(secret: string): Promise<LoginResponse> {
  const response = await apiPost<LoginResponse>('/login-secret', {
    secret,
    includeToken: false,
    includeSecret: false,
    persona: LOGIN_PERSONA,
  });
  persistLocalCredentials(response);
  return response;
}

export async function resendSecretLink(secret: string): Promise<void> {
  await apiPost('/resend-secret', { secret });
}

/**
 * Soft reset for a same-device identity swap initiated by /login-secret. The
 * BE already rotated the session cookies, so /auth/logout must NOT be called.
 */
export function resetLocalStateForProfileSwap(): void {
  disconnectWs();
  setActiveProfile(null);
  resetPosthog();
  clearAuthState();
  if (!isCookieMode) {
    localStorage.removeItem(JWT_STORAGE_KEY);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    localStorage.removeItem(SECRET_STORAGE_KEY);
  }
  useAuthStore.getState().setAuthenticated(false);
  useAuthStore.getState().setSessionStatus('idle');
}

/**
 * Tears the session down: detaches the device while the session is still
 * valid, then asks the BE to clear the cookies, then clears local state. The
 * order matters — detaching after /auth/logout would 401.
 */
async function endSession(): Promise<void> {
  disconnectWs();
  try {
    await logoutDeviceOnBackend();
  } catch {
    /* best-effort */
  }
  if (isCookieMode) {
    try {
      await apiPost('/auth/logout', {});
    } catch {
      /* best-effort */
    }
  }
  // The CSRF cookie is JS-readable and is the "signed in" sentinel. If the
  // BE didn't clear it (a stale or foreign cookie the BE never issued to this
  // session), expire the host-only copy so the next boot doesn't read it as
  // a session again.
  if (isCookieMode && typeof document !== 'undefined') {
    document.cookie = `${CSRF_COOKIE_NAME}=; Path=/; Max-Age=0`;
  }
  setActiveProfile(null);
  resetPosthog();
  clearAuthState();
  localStorage.removeItem(JWT_STORAGE_KEY);
  localStorage.removeItem(EMAIL_STORAGE_KEY);
  localStorage.removeItem(SECRET_STORAGE_KEY);
  useAuthStore.getState().setAuthenticated(false);
  useAuthStore.getState().setSessionStatus('idle');
}

/**
 * Clears local auth state and tells the BE to clear cookies and detach the
 * device — without reloading. Used when the BE says there was never a
 * credential (reloading would re-read a leaked sibling-env sentinel and loop).
 */
export async function logoutSoft(): Promise<void> {
  setLogoutInProgress(true);
  try {
    await endSession();
  } finally {
    setLogoutInProgress(false);
  }
}

/**
 * Full logout, then a reload so every cache starts clean. The in-progress
 * flag is never cleared — the page is leaving. If tearing down fails, fall
 * back to clearing the BE session and going home.
 */
export async function logout(): Promise<void> {
  setLogoutInProgress(true);
  try {
    await endSession();
  } catch {
    try {
      await apiPost('/auth/logout', {});
    } catch {
      /* best-effort */
    }
    if (typeof window !== 'undefined') window.location.assign('/');
    return;
  }
  if (typeof window !== 'undefined') window.location.assign('/sign-in');
}

/**
 * Loads the signed-in user and selects the company profile. Sets
 * `sessionStatus` to `ready`, or to `no-access` (and throws
 * NoAdminProfileError) when the account has no admin-persona profile.
 */
export async function whoAmI(profileId?: string): Promise<Profile> {
  let user: User;
  try {
    user = await apiGet<User>('/who-am-i', { persona: personaQueryParam });
  } catch (err) {
    if (isApiError(err) && err.status === 401) useAuthStore.getState().setSessionStatus('idle');
    throw err;
  }
  setLogLevels(user.logLevels);

  const selected = profileId
    ? user.profiles.find((p) => p.id === profileId)
    : user.profiles.find((p) => SUPPORTED_PERSONAS.includes(p.persona));

  if (!selected) {
    useAuthStore.getState().setSessionStatus('no-access');
    throw new NoAdminProfileError();
  }

  setActiveProfileId(selected.id);
  await setDeviceUserId(selected.userId);

  const [profile] = await Promise.all([getProfileById(selected.id), putDeviceNow()]);
  setActiveProfile(profile);
  identifyPosthogProfile(profile);
  if (profile.metadata?.logLevels) setLogLevels(profile.metadata.logLevels);

  // Admin date filters resolve `startDate`/`endDate` in the profile's
  // timezone (BE reqs §0.2), so a profile without one is stamped with the
  // browser's.
  if (!profile.timezone) {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    profile.timezone = browserTimezone;
    await patchProfile({ id: profile.id, timezone: browserTimezone });
  }

  useAuthStore.getState().setSessionStatus('ready');
  return profile;
}

/** Account page: request a code for a NEW phone number on the signed-in profile. */
export async function requestPhoneChangeCode(phone: string): Promise<void> {
  await apiPut<void>('/access-code/sms', {}, { phone, persona: LOGIN_PERSONA });
}

/** Account page: confirm the phone change. The BE reads the target number it stored at send time. */
export async function confirmPhoneChange(code: string): Promise<Profile> {
  return apiPost<Profile>('/login-code', { password: code });
}
